import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Clock, Loader2 } from "lucide-react";
import { fetchRecipes } from "../lib/api";
import { PRODUCTS } from "../lib/brand";
import { Navigation } from "../components/landing/Navigation";
import { Footer } from "../components/landing/Footer";
import { FloatingWhatsApp } from "../components/landing/FloatingWhatsApp";

const HeatDots = ({ level }) => (
  <span className="inline-flex items-center gap-1">
    <Flame size={12} className="text-[#D99026]" />
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: i < level ? "#D99026" : "rgba(255,255,255,0.3)" }}
      />
    ))}
  </span>
);

const FILTERS = [
  { id: "all", label: "All recipes" },
  ...PRODUCTS.map((p) => ({ id: p.slug, label: p.name })),
];

const RecipesIndex = () => {
  const [filter, setFilter] = useState("all");
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    fetchRecipes()
      .then((data) => {
        if (!ignore) setRecipes(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, []);

  const list = useMemo(
    () => (filter === "all" ? recipes : recipes.filter((r) => r.spice_slug === filter)),
    [filter, recipes],
  );

  return (
    <main data-testid="recipes-index-page" className="min-h-screen bg-mo-cream">
      <Navigation />

      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 kraft-grain">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
            — The Maati Kitchen
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-mo-ink leading-[0.98] tracking-tight max-w-4xl">
            Recipes that taste of <span className="italic text-mo-green">home.</span>
          </h1>
          <p className="mt-6 text-mo-ink-soft max-w-2xl text-lg">
            Slow dishes, weeknight wonders, and the doodh our grandmothers swore
            by — cooked with the three Maati tins and a lot of patience.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`recipes-filter-${f.id}`}
                className={`px-5 py-2.5 rounded-full text-sm transition-all duration-300 border ${
                  filter === f.id
                    ? "bg-mo-ink text-mo-cream border-mo-ink"
                    : "bg-mo-cream text-mo-ink-soft border-[#5C5248]/20 hover:border-mo-terracotta hover:text-mo-terracotta"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="relative pb-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="py-24 flex items-center justify-center text-mo-ink-soft">
              <Loader2 className="animate-spin mr-3" size={18} /> Brewing recipes…
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-20 text-mo-ink-soft">
              No recipes in this tin yet — come back soon.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {list.map((r) => (
                <Link
                  key={r.slug}
                  to={`/recipes/${r.slug}`}
                  data-testid={`recipe-tile-${r.slug}`}
                  className="group block overflow-hidden rounded-2xl relative aspect-[4/5] shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-500"
                >
                  <img
                    src={r.image}
                    alt={r.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C2520] via-[#2C2520]/30 to-transparent" />
                  <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.22em] text-mo-cream bg-mo-cream/15 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20">
                    {r.spice_name}
                  </div>
                  <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-mo-cream/90 flex items-center justify-center text-mo-green group-hover:bg-[#D99026] transition-colors duration-300">
                    <ArrowUpRight size={18} />
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 text-mo-cream">
                    <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.2em] text-[#D99026] mb-2">
                      <span className="flex items-center gap-1">
                        <Clock size={11} /> {r.cook}
                      </span>
                      <HeatDots level={r.heat} />
                    </div>
                    <h3 className="font-serif-display text-2xl leading-tight">{r.name}</h3>
                    <p className="text-sm text-mo-cream/80 mt-2 line-clamp-2">{r.intro}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
};

export default RecipesIndex;
